import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const SocialFeed = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [twitterPosts, setTwitterPosts] = useState([]);
  const [linkedinPosts, setLinkedinPosts] = useState([]);
  const [farcasterPosts, setFarcasterPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch tweets from our serverless function or localStorage
  useEffect(() => {
    const fetchTweets = async () => {
      setIsLoading(true);
      // Otherwise fetch fresh data from our serverless function
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/social/tweets`);
        const data = await response.json();
        
        if (data && data.length > 0) {
          // Transform Twitter API data to match our format
          const transformedTweets = data.map(tweet => ({
            id: tweet.id,
            platform: 'twitter',
            author: 'Nikunj Navdiya',
            handle: '@navdiya_nikunj',
            avatar: '/avatar.jpg',
            content: tweet.content,
            timestamp: formatTwitterDate(tweet.createdAt),
            category: tweet.category,
            world: tweet.world,
          }));
          
          // Save to state and localStorage
          setTwitterPosts(transformedTweets);
        }
      } catch (error) {
        console.error('Error fetching tweets:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTweets();
  }, []);

  useEffect(() => {
    const fetchLinkedinPosts = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/social/linkedin`);
        const data = await response.json();
        if (data && data.length > 0) {
          const transformedLinkedinPosts = data.map(post => ({
            id: post.id,
            platform: 'linkedin',
            author: 'Nikunj Navdiya',
            handle: 'Navdiya Nikunj',
            avatar: '/avatar.jpg',
            content: post.content,
            timestamp: formatTwitterDate(post.createdAt),
            category: post.category,
            world: post.world,
          }));  
          setLinkedinPosts(transformedLinkedinPosts);
        }
      } catch (error) {
        console.error('Error fetching linkedin posts:', error);
      }
    };
    fetchLinkedinPosts();
  }, []);

  useEffect(() => {
    const fetchFarcasterCasts = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/social/farcaster`);
        const data = await response.json()
        if (data && data.length > 0) {
          const transformedFarcasterCasts = data.map(cast => ({
            id: cast.id,
            platform: 'farcaster',
            author: 'Nikunj Navdiya',
            handle: '@nik-navdiya',
            avatar: '/avatar.jpg',
            content: cast.content,
            timestamp: formatTwitterDate(cast.createdAt),
            category: cast.category,
            world: cast.world,
          }));
          setFarcasterPosts(transformedFarcasterCasts);
        }
      } catch (error) {
        console.error('Error fetching farcaster casts:', error);
      }
    };
    fetchFarcasterCasts();
  }, []);


  // Helper function to format Twitter date
  const formatTwitterDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) return `${diffSec} sec ago`;
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHour < 24) return `${diffHour} hours ago`;
    if (diffDay < 7) return `${diffDay} days ago`;
    
    return date.toLocaleDateString();
  };
  


  
  // Combine Twitter posts with static posts
  const socialPosts = [...twitterPosts, ...linkedinPosts, ...farcasterPosts];

  const platforms = [
    { id: 'all', label: 'All Posts', icon: <Icon name="Globe" size={16} /> },
    { id: 'twitter', label: 'X (Twitter)', icon: <Image src="/icons/twitter.svg" alt="Twitter" width={16} height={16} /> },
    { id: 'linkedin', label: 'LinkedIn', icon: <Icon name="Linkedin" size={16} /> },
    { id: 'farcaster', label: 'Farcaster', icon: <Image src="/icons/farcaster.svg" alt="Farcaster" width={16} height={16} /> }
  ];

  const categories = [
    { id: 'all', label: 'All', color: 'primary' },
    { id: 'web3', label: 'Web3', color: 'warning' },
    { id: 'community', label: 'Community', color: 'success' },
    { id: 'software', label: 'Software', color: 'primary' }
  ];

  const filteredPosts = socialPosts.filter(post => {
    if (activeTab === 'all') return true;
    return post.platform === activeTab || post.category === activeTab;
  });

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'twitter': return <Image src="/icons/twitter.svg" alt="Twitter" width={16} height={16} />;
      case 'linkedin': return <Icon name="Linkedin" size={16} />;
      case 'farcaster': return <Image src="/icons/farcaster.svg" alt="Farcaster" width={16} height={16} />;
      default: return <Icon name="Globe" size={16} />;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'twitter': return 'text-blue-500';
      case 'linkedin': return 'text-blue-600';
      case 'farcaster': return 'text-purple-500';
      default: return 'text-text-secondary';
    }
  };

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            Social{' '}
            <span className="bg-gradient-to-r from-accent to-warning bg-clip-text text-transparent">
              Updates
            </span>
          </h2>
          <p className="font-body text-text-secondary text-lg max-w-3xl mx-auto">
            Stay connected with my latest thoughts, projects, and insights across all platforms. Follow my journey in real-time.
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {[...platforms, ...categories.slice(1)].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-body font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-primary text-surface shadow-elevation-2'
                  : 'bg-background text-text-secondary hover:bg-primary/10 hover:text-primary'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </motion.div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Social Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-background border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300 group"
            >
              {/* Post Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <Image
                      src={post.avatar}
                      alt={post.author}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className={`absolute -bottom-1 -right-1 w-6 h-6 bg-surface rounded-full flex items-center justify-center border-2 border-surface`}>
                      {getPlatformIcon(post.platform)}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-primary">{post.author}</h4>
                    <p className="font-caption text-text-secondary text-sm">{post.handle}</p>
                  </div>
                </div>
                
              </div>

              {/* Post Content */}
              <div className="mb-4">
                <p className="font-body text-text-primary leading-relaxed whitespace-pre-line">
                  {post.content}
                </p>
              </div>

              {/* Post Stats */}
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center space-x-4">
                  
                </div>
                {post.platform === 'twitter' && (
                  <a 
                    href={`https://twitter.com/user/status/${post.id}`}
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-text-secondary hover:text-primary transition-colors duration-200"
                  >
                    <Icon name="ExternalLink" size={16} />
                  </a>
                )}
                {post.platform === 'linkedin' && (
                  <a 
                    href={`https://www.linkedin.com/feed/update/urn:li:activity:${post.id}`}
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-text-secondary hover:text-primary transition-colors duration-200"
                  >
                    <Icon name="ExternalLink" size={16} />
                  </a>
                )}
                {post.platform === 'farcaster' && (
                  <a 
                    href={`https://farcaster.xyz/post/${post.id}`}
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-text-secondary hover:text-primary transition-colors duration-200"
                  >
                    <Icon name="ExternalLink" size={16} />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="font-body text-text-secondary mb-6">
            Follow me on your favorite platform for more updates
          </p>
          <div className="flex flex-col md:flex-row justify-center space-x-4">
            {[
              { platform: 'Twitter', icon: <Image src="/icons/twitter.svg" alt="Twitter" width={16} height={16} />, color: 'hover:bg-blue-500', handle: '@navdiya_nikunj', link: 'https://x.com/navdiya_nikunj' },
              { platform: 'LinkedIn', icon: <Icon name="Linkedin" size={16} />, color: 'hover:bg-blue-600', handle: 'Navdiya Nikunj', link: 'https://www.linkedin.com/in/nikunj-navdiya-024543235/' },
              { platform: 'Farcaster', icon: <Image src="/icons/farcaster.svg" alt="Farcaster" width={16} height={16} />, color: 'hover:bg-purple-500', handle: '@nik-navdiya', link: 'https://farcaster.xyz/nik-navdiya' }
            ].map((social) => (
              <button
                key={social.platform}
                onClick={() => window.open(social.link, '_blank')}
                className={`flex items-center space-x-2 px-6 py-3 bg-surface border border-border rounded-lg font-body font-medium text-text-secondary hover:text-surface transition-all duration-200 ${social.color}`}
              >
                {social.icon}
                <span>{social.handle}</span>
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SocialFeed;